import { css } from '@emotion/react';

/** Marks the row a RowKebabMenu lives in: spread KEBAB_HOST_ROW_PROPS onto the row element itself, whose hover reveals the kebab. */
const KEBAB_HOST_ROW_ATTRIBUTE = 'data-kebab-host-row';
export const KEBAB_HOST_ROW_PROPS = { [KEBAB_HOST_ROW_ATTRIBUTE]: 'true' } as const;
const HOST_ROW = `[${KEBAB_HOST_ROW_ATTRIBUTE}]`;
const KEBAB_MENU_OPEN = `button[aria-haspopup='menu'][aria-expanded='true']`;

/** Width of the gutter a row's kebab opens in its trailing cluster: clears the 22px trigger sitting 16px from the row's edge. */
export const KEBAB_GUTTER_WIDTH = 34;

/** How long the gutter's slide takes, in and out. */
const KEBAB_GUTTER_SLIDE_MS = 133;

/** How long the pointer must rest on a row before its gutter opens, so a quick pass across a list moves nothing. */
const KEBAB_GUTTER_HOVER_INTENT_MS = 250;

/** Always applied to a trailing cluster, open or not, so the gutter slides back as well as open. */
export const KEBAB_GUTTER_TRANSITION_STYLE = css`
  label: KEBAB_GUTTER_TRANSITION_STYLE;
  transition: transform ${KEBAB_GUTTER_SLIDE_MS}ms ease-out;
`;

/**
 * Slides the trailing cluster left on hover, or while its kebab's menu is open, to clear the gutter.
 * Apply only where the row has a kebab. The hover-intent delay applies to opening only.
 */
export const KEBAB_GUTTER_HOVER_STYLE = css`
  label: KEBAB_GUTTER_HOVER_STYLE;
  ${HOST_ROW}:hover &,
  ${HOST_ROW}:has(${KEBAB_MENU_OPEN}) & {
    transform: translateX(-${KEBAB_GUTTER_WIDTH}px);
    transition-delay: ${KEBAB_GUTTER_HOVER_INTENT_MS}ms;
  }
`;

/**
 * The same gutter, held open: for a row whose controls must line up with its neighbors' (the navigator's approve buttons).
 * Reserves the gutter in layout with a margin so the rest of the row makes room for it, and cancels the hover slide so the
 * cluster doesn't shift twice. List it after KEBAB_GUTTER_HOVER_STYLE so its equal-specificity rules win.
 * A navigator task row holds it open only when it also has a kebab: a task keeps offering approval in closeout, where no
 * header renders one to line up with (see KaiNavTaskRow's `holdsKebabGutterOpen`).
 */
export const KEBAB_GUTTER_OPEN_STYLE = css`
  label: KEBAB_GUTTER_OPEN_STYLE;
  margin-right: ${KEBAB_GUTTER_WIDTH}px;
  transform: none;

  ${HOST_ROW}:hover &,
  ${HOST_ROW}:has(${KEBAB_MENU_OPEN}) & {
    transform: none;
  }
`;

/**
 * Raises a row above its neighbors while its kebab menu is open, so a later sibling can't paint
 * over the menu. Apply to the row itself, which must be `position: relative` or `sticky`.
 */
export const KEBAB_MENU_OPEN_RAISE_STYLE = css`
  label: KEBAB_MENU_OPEN_RAISE_STYLE;
  &:has(${KEBAB_MENU_OPEN}) {
    z-index: 75;
  }
`;

/** The trigger appears once the gutter has slid halfway open, never over a control still moving out of the way. */
const KEBAB_TRIGGER_REVEAL_DELAY_MS = KEBAB_GUTTER_HOVER_INTENT_MS + Math.round(KEBAB_GUTTER_SLIDE_MS / 2);

export const KEBAB_TRIGGER_REVEAL_STYLE = css`
  label: KEBAB_TRIGGER_REVEAL_STYLE;
  visibility: hidden;

  ${HOST_ROW}:hover &,
  &:focus-visible,
  &[aria-expanded='true'] {
    visibility: visible;
    transition: color 120ms ease, background 120ms ease, visibility 0s linear ${KEBAB_TRIGGER_REVEAL_DELAY_MS}ms;
  }

  /* touch has no hover to reveal it with */
  @media (hover: none), (pointer: coarse) {
    visibility: visible;
  }
`;
